import { C } from "../constants/colors";

const ff = "'Outfit',sans-serif";
const label = (x, y, text, color, size=11, anchor="middle", weight="400") => (
  <text x={x} y={y} fontSize={size} fill={color||C.textMid} fontFamily={ff} textAnchor={anchor} fontWeight={weight}>{text}</text>
);

// ═══════════════════════════════════════
//  LIGHT — REFLECTION (Class 10 Ch 9)
// ═══════════════════════════════════════

export function SvgReflectionLaw() {
  return (
    <svg viewBox="0 0 400 220" xmlns="http://www.w3.org/2000/svg" style={{width:"100%",maxWidth:400}}>
      {label(200, 18, "Laws of Reflection", C.text, 13, "middle", "600")}
      {/* Mirror surface */}
      <line x1="80" y1="160" x2="320" y2="160" stroke={C.textMid} strokeWidth="3"/>
      {/* Hatching */}
      {[90,110,130,150,170,190,210,230,250,270,290,310].map(x =>
        <line key={x} x1={x} y1="160" x2={x-10} y2="175" stroke={C.textMute} strokeWidth="1"/>
      )}
      {/* Normal */}
      <line x1="200" y1="160" x2="200" y2="40" stroke={C.textMid} strokeWidth="1.5" strokeDasharray="5,3"/>
      {label(210, 38, "Normal", C.textMid, 10, "start")}
      {/* Incident ray */}
      <line x1="100" y1="50" x2="200" y2="160" stroke={C.amber} strokeWidth="2.5"/>
      <polygon points="155,110 145,103 152,115" fill={C.amber}/>
      {label(130, 80, "Incident ray", C.amber, 10, "middle", "600")}
      {/* Reflected ray */}
      <line x1="200" y1="160" x2="300" y2="50" stroke="#E24B4A" strokeWidth="2.5"/>
      <polygon points="255,100 248,108 258,105" fill="#E24B4A"/>
      {label(275, 80, "Reflected ray", "#E24B4A", 10, "middle", "600")}
      {/* Angle arcs */}
      <path d="M 195 130 A 30 30 0 0 0 178 138" fill="none" stroke={C.amber} strokeWidth="2"/>
      {label(178, 125, "i", C.amber, 13, "middle", "600")}
      <path d="M 205 130 A 30 30 0 0 1 222 138" fill="none" stroke="#E24B4A" strokeWidth="2"/>
      {label(222, 125, "r", "#E24B4A", 13, "middle", "600")}
      {/* Point of incidence */}
      <circle cx="200" cy="160" r="4" fill={C.primaryMid}/>
      {/* Law */}
      {label(200, 200, "∠i = ∠r (angle of incidence = angle of reflection)", C.text, 11, "middle", "600")}
    </svg>
  );
}

export function SvgConcaveMirror() {
  return (
    <svg viewBox="0 0 400 220" xmlns="http://www.w3.org/2000/svg" style={{width:"100%",maxWidth:400}}>
      {label(200, 16, "Concave Mirror — Ray Diagram", C.text, 13, "middle", "600")}
      {/* Mirror curve */}
      <path d="M 310 40 Q 280 120 310 200" fill="none" stroke={C.textMid} strokeWidth="3"/>
      {/* Hatching */}
      {[50,70,90,110,130,150,170,190].map((_,i) => {
        const y = 45 + i*20;
        return <line key={i} x1="310" y1={y} x2="320" y2={y} stroke={C.textMute} strokeWidth="1"/>;
      })}
      {/* Principal axis */}
      <line x1="20" y1="120" x2="340" y2="120" stroke={C.textMid} strokeWidth="1"/>
      {/* Key points */}
      <circle cx="305" cy="120" r="3" fill={C.textMid}/>
      {label(305, 135, "P", C.textMid, 10)}
      <circle cx="245" cy="120" r="3" fill={C.primaryMid}/>
      {label(245, 135, "F", C.primaryMid, 10, "middle", "600")}
      <circle cx="185" cy="120" r="3" fill={C.primaryMid}/>
      {label(185, 135, "C", C.primaryMid, 10, "middle", "600")}
      {/* Object */}
      <line x1="120" y1="120" x2="120" y2="65" stroke={C.amber} strokeWidth="2.5"/>
      <polygon points="120,65 115,72 125,72" fill={C.amber}/>
      {label(110, 58, "Object", C.amber, 10, "end", "600")}
      {/* Ray 1: parallel to axis → through F */}
      <line x1="120" y1="65" x2="305" y2="65" stroke={C.primaryMid} strokeWidth="1.5"/>
      <line x1="305" y1="65" x2="245" y2="120" stroke={C.primaryMid} strokeWidth="1.5"/>
      {/* Extend to image */}
      <line x1="245" y1="120" x2="210" y2="155" stroke={C.primaryMid} strokeWidth="1.5" strokeDasharray="4,3"/>
      {/* Ray 2: through C → reflects back */}
      <line x1="120" y1="65" x2="305" y2="96" stroke="#E24B4A" strokeWidth="1.5"/>
      <line x1="305" y1="96" x2="210" y2="155" stroke="#E24B4A" strokeWidth="1.5" strokeDasharray="4,3"/>
      {/* Image */}
      <line x1="210" y1="120" x2="210" y2="155" stroke={C.purpleMid} strokeWidth="2.5"/>
      <polygon points="210,155 205,148 215,148" fill={C.purpleMid}/>
      {label(210, 168, "Image", C.purpleMid, 10, "middle", "600")}
      {label(210, 180, "(real, inverted)", C.textMute, 9)}
      {/* Formula */}
      {label(200, 210, "Mirror formula: 1/f = 1/v + 1/u", C.text, 11, "middle", "600")}
    </svg>
  );
}

export function SvgConvexMirror() {
  return (
    <svg viewBox="0 0 400 200" xmlns="http://www.w3.org/2000/svg" style={{width:"100%",maxWidth:400}}>
      {label(200, 16, "Convex Mirror — Ray Diagram", C.text, 13, "middle", "600")}
      {/* Mirror curve */}
      <path d="M 90 35 Q 120 100 90 175" fill="none" stroke={C.textMid} strokeWidth="3"/>
      {/* Principal axis */}
      <line x1="60" y1="105" x2="380" y2="105" stroke={C.textMid} strokeWidth="1"/>
      {/* Key points */}
      <circle cx="95" cy="105" r="3" fill={C.textMid}/>
      {label(95, 120, "P", C.textMid, 10)}
      <circle cx="155" cy="105" r="3" fill={C.primaryMid}/>
      {label(155, 120, "F", C.primaryMid, 10, "middle", "600")}
      <circle cx="215" cy="105" r="3" fill={C.primaryMid}/>
      {label(215, 120, "C", C.primaryMid, 10, "middle", "600")}
      {/* Object */}
      <line x1="300" y1="105" x2="300" y2="55" stroke={C.amber} strokeWidth="2.5"/>
      <polygon points="300,55 295,62 305,62" fill={C.amber}/>
      {label(310, 50, "Object", C.amber, 10, "start", "600")}
      {/* Ray 1: parallel → appears from F */}
      <line x1="300" y1="55" x2="94" y2="55" stroke={C.primaryMid} strokeWidth="1.5"/>
      <line x1="94" y1="55" x2="300" y2="55" stroke={C.primaryMid} strokeWidth="0"/>
      <line x1="94" y1="55" x2="50" y2="30" stroke={C.primaryMid} strokeWidth="1.5"/>
      <line x1="155" y1="105" x2="94" y2="55" stroke={C.primaryMid} strokeWidth="1" strokeDasharray="4,3"/>
      {/* Virtual image */}
      <line x1="140" y1="105" x2="140" y2="88" stroke={C.purpleMid} strokeWidth="2.5" strokeDasharray="5,3"/>
      <polygon points="140,88 135,95 145,95" fill={C.purpleMid}/>
      {label(140, 130, "Image", C.purpleMid, 10, "middle", "600")}
      {label(140, 142, "(virtual, erect,", C.textMute, 8, "middle")}
      {label(140, 152, "diminished)", C.textMute, 8, "middle")}
      {label(200, 185, "Convex mirror: always virtual, erect, smaller image", C.textMid, 10)}
    </svg>
  );
}

// ═══════════════════════════════════════
//  LIGHT — REFRACTION
// ═══════════════════════════════════════

export function SvgRefraction() {
  return (
    <svg viewBox="0 0 400 240" xmlns="http://www.w3.org/2000/svg" style={{width:"100%",maxWidth:400}}>
      {label(200, 18, "Refraction of Light", C.text, 13, "middle", "600")}
      {/* Air region */}
      <rect x="40" y="30" width="320" height="90" fill="#E6F1FB" rx="4"/>
      {label(70, 50, "Air (rarer)", C.blueMid, 10, "start", "600")}
      {/* Water region */}
      <rect x="40" y="120" width="320" height="100" fill="#E1F5EE" rx="4"/>
      {label(70, 210, "Water (denser)", C.primaryMid, 10, "start", "600")}
      {/* Interface */}
      <line x1="40" y1="120" x2="360" y2="120" stroke={C.textMid} strokeWidth="2"/>
      {/* Normal */}
      <line x1="200" y1="40" x2="200" y2="210" stroke={C.textMid} strokeWidth="1" strokeDasharray="4,3"/>
      {label(208, 42, "Normal", C.textMid, 9, "start")}
      {/* Incident ray */}
      <line x1="120" y1="40" x2="200" y2="120" stroke={C.amber} strokeWidth="2.5"/>
      <polygon points="165,85 155,80 160,90" fill={C.amber}/>
      {/* Refracted ray — bends toward normal in denser medium */}
      <line x1="200" y1="120" x2="260" y2="210" stroke="#E24B4A" strokeWidth="2.5"/>
      <polygon points="235,170 228,165 232,176" fill="#E24B4A"/>
      {/* Angle labels */}
      <path d="M 195 95 A 25 25 0 0 0 180 103" fill="none" stroke={C.amber} strokeWidth="2"/>
      {label(172, 90, "∠i", C.amber, 12, "middle", "600")}
      <path d="M 205 145 A 25 25 0 0 1 218 138" fill="none" stroke="#E24B4A" strokeWidth="2"/>
      {label(225, 148, "∠r", "#E24B4A", 12, "middle", "600")}
      {/* Key point */}
      <circle cx="200" cy="120" r="4" fill={C.text}/>
      {label(200, 235, "Light bends toward normal when entering denser medium (∠i > ∠r)", C.textMid, 10)}
    </svg>
  );
}

export function SvgConvexLens() {
  return (
    <svg viewBox="0 0 400 200" xmlns="http://www.w3.org/2000/svg" style={{width:"100%",maxWidth:400}}>
      {label(200, 16, "Convex Lens — Image Formation", C.text, 13, "middle", "600")}
      {/* Lens */}
      <ellipse cx="200" cy="100" rx="8" ry="65" fill="none" stroke={C.primaryMid} strokeWidth="2.5"/>
      {/* Principal axis */}
      <line x1="20" y1="100" x2="380" y2="100" stroke={C.textMid} strokeWidth="1"/>
      {/* Focal points */}
      <circle cx="140" cy="100" r="3" fill={C.primaryMid}/>
      {label(140, 115, "F₁", C.primaryMid, 10, "middle", "600")}
      <circle cx="260" cy="100" r="3" fill={C.primaryMid}/>
      {label(260, 115, "F₂", C.primaryMid, 10, "middle", "600")}
      {/* 2F points */}
      <circle cx="80" cy="100" r="2.5" fill={C.textMute}/>
      {label(80, 115, "2F₁", C.textMute, 9)}
      <circle cx="320" cy="100" r="2.5" fill={C.textMute}/>
      {label(320, 115, "2F₂", C.textMute, 9)}
      {/* Optical center */}
      {label(200, 115, "O", C.textMid, 10)}
      {/* Object */}
      <line x1="100" y1="100" x2="100" y2="50" stroke={C.amber} strokeWidth="2.5"/>
      <polygon points="100,50 95,57 105,57" fill={C.amber}/>
      {label(88, 45, "Object", C.amber, 10, "end", "600")}
      {/* Ray 1: parallel → through F2 */}
      <line x1="100" y1="50" x2="200" y2="50" stroke={C.primaryMid} strokeWidth="1.5"/>
      <line x1="200" y1="50" x2="310" y2="140" stroke={C.primaryMid} strokeWidth="1.5"/>
      {/* Ray 2: through O → straight */}
      <line x1="100" y1="50" x2="310" y2="140" stroke="#E24B4A" strokeWidth="1.5"/>
      {/* Image */}
      <line x1="310" y1="100" x2="310" y2="140" stroke={C.purpleMid} strokeWidth="2.5"/>
      <polygon points="310,140 305,133 315,133" fill={C.purpleMid}/>
      {label(318, 148, "Image", C.purpleMid, 10, "start", "600")}
      {/* Formula */}
      {label(200, 185, "Lens formula: 1/f = 1/v − 1/u", C.text, 11, "middle", "600")}
    </svg>
  );
}

export function SvgDispersion() {
  return (
    <svg viewBox="0 0 400 200" xmlns="http://www.w3.org/2000/svg" style={{width:"100%",maxWidth:400}}>
      {label(200, 16, "Dispersion of Light through a Prism", C.text, 13, "middle", "600")}
      {/* Prism */}
      <polygon points="200,35 130,160 270,160" fill="none" stroke={C.textMid} strokeWidth="2.5"/>
      {label(200, 110, "Prism", C.textMid, 10, "middle")}
      {/* White light ray */}
      <line x1="40" y1="110" x2="155" y2="105" stroke={C.textMid} strokeWidth="3"/>
      {label(50, 100, "White light", C.textMid, 10, "start", "600")}
      {/* Spectrum rays - VIBGYOR */}
      <line x1="245" y1="95" x2="360" y2="50" stroke="#9333ea" strokeWidth="2"/>
      <line x1="245" y1="100" x2="360" y2="65" stroke="#3b82f6" strokeWidth="2"/>
      <line x1="245" y1="105" x2="360" y2="80" stroke="#06b6d4" strokeWidth="2"/>
      <line x1="245" y1="110" x2="360" y2="95" stroke="#22c55e" strokeWidth="2"/>
      <line x1="245" y1="115" x2="360" y2="110" stroke="#eab308" strokeWidth="2"/>
      <line x1="245" y1="120" x2="360" y2="125" stroke="#f97316" strokeWidth="2"/>
      <line x1="245" y1="125" x2="360" y2="140" stroke="#ef4444" strokeWidth="2"/>
      {/* Labels */}
      {label(375, 53, "V", "#9333ea", 9, "start", "600")}
      {label(375, 68, "I", "#3b82f6", 9, "start", "600")}
      {label(375, 83, "B", "#06b6d4", 9, "start", "600")}
      {label(375, 98, "G", "#22c55e", 9, "start", "600")}
      {label(375, 113, "Y", "#eab308", 9, "start", "600")}
      {label(375, 128, "O", "#f97316", 9, "start", "600")}
      {label(375, 143, "R", "#ef4444", 9, "start", "600")}
      {label(200, 185, "White light splits into 7 colors (VIBGYOR)", C.textMid, 11)}
    </svg>
  );
}

export function SvgHumanEye() {
  return (
    <svg viewBox="0 0 400 220" xmlns="http://www.w3.org/2000/svg" style={{width:"100%",maxWidth:400}}>
      {label(200, 16, "Human Eye — Structure", C.text, 13, "middle", "600")}
      {/* Eye outline */}
      <ellipse cx="200" cy="115" rx="120" ry="75" fill="#F8FAFC" stroke={C.textMid} strokeWidth="2"/>
      {/* Cornea */}
      <path d="M 85 80 Q 65 115 85 150" fill="none" stroke={C.primaryMid} strokeWidth="3"/>
      {label(52, 115, "Cornea", C.primaryMid, 9, "end", "600")}
      {/* Iris + Pupil */}
      <ellipse cx="105" cy="115" rx="5" ry="25" fill={C.amberSoft} stroke={C.amberDark} strokeWidth="1.5"/>
      <ellipse cx="105" cy="115" rx="3" ry="12" fill={C.text}/>
      {label(105, 150, "Iris", C.amberDark, 8, "middle")}
      {label(105, 160, "& Pupil", C.amberDark, 8, "middle")}
      {/* Lens */}
      <ellipse cx="140" cy="115" rx="10" ry="30" fill="none" stroke={C.purpleMid} strokeWidth="2"/>
      {label(140, 85, "Lens", C.purpleMid, 9, "middle", "600")}
      {/* Retina */}
      <path d="M 310 70 Q 330 115 310 160" fill="none" stroke="#E24B4A" strokeWidth="3"/>
      {label(340, 115, "Retina", "#E24B4A", 9, "start", "600")}
      {/* Optic nerve */}
      <line x1="320" y1="115" x2="360" y2="115" stroke={C.textMid} strokeWidth="4"/>
      {label(365, 115, "Optic", C.textMid, 8, "start")}
      {label(365, 125, "nerve", C.textMid, 8, "start")}
      {/* Light rays */}
      <line x1="20" y1="90" x2="85" y2="105" stroke={C.amber} strokeWidth="1" strokeDasharray="4,3"/>
      <line x1="20" y1="140" x2="85" y2="125" stroke={C.amber} strokeWidth="1" strokeDasharray="4,3"/>
      {/* Focus on retina */}
      <circle cx="305" cy="115" r="4" fill="#E24B4A" opacity="0.5"/>
      {label(200, 200, "Light → Cornea → Pupil → Lens → Retina → Optic nerve → Brain", C.textMid, 10)}
    </svg>
  );
}

// ═══════════════════════════════════════
//  EXPORT MAP
// ═══════════════════════════════════════

export const LIGHT_SVG_MAP = {
  "reflection-law": SvgReflectionLaw,
  "concave-mirror": SvgConcaveMirror,
  "convex-mirror": SvgConvexMirror,
  "refraction": SvgRefraction,
  "convex-lens": SvgConvexLens,
  "dispersion": SvgDispersion,
  "human-eye": SvgHumanEye,
};
